import React, { useEffect, useState, useMemo } from 'react';
import { Download, ShieldCheck, IdCard, LogOut, XCircle, MessageCircle } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Cookies from "js-cookie";
import {
    getInfoAlunoByCpf,
    getUserCarteirinhaByCpfPngApi,
    generatePdfCarteirinhaDigitalApi,
    generatePdfValidadorDigitalApi,
} from '../../Api';
import './styleMinhaCarteira.css';
import { whatsappUrl } from '../../config';
import { getWalletStatus } from './getWalletStatus';
import CardInfoCarteira from './CardInfoCarteira';
import Footer from '../../components/Footer';
import BeneficiosEAutenticacao from './BeneficiosEAutenticacao';
import CardValidacaoDigital from './CardValidacaoDigital';

const STATUS_TEXT = {
    active: 'Documento ativo',
    warning: 'Renovação próxima',
    expired: 'Documento expirado',
    loading: 'Verificando…',
};

// Comprovante de validação (PDF assinado, /api/validarpdf) temporariamente
// DESATIVADO em produção enquanto a assinatura é ajustada.
// Vire para true para reativar o botão e o texto correspondente.
const COMPROVANTE_VALIDACAO_ATIVO = false;

// Contato para renovação (número central em src/config.js).
const contatoUrl = (cpf) =>
    whatsappUrl(`Olá! Minha carteirinha UNAE está vencida${cpf ? ` (CPF ${cpf})` : ''} e gostaria de renová-la.`);

export default function MinhaCarteira() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const cpf = Cookies.get("cpf");

    // TESTE: força o status do documento pela URL p/ ver a UI sem mexer na validade.
    // Ex.: /visualizaminhacarteira?simular=expired (ou warning | active).
    const simular = searchParams.get('simular');

    const [estudante, setEstudante] = useState(null);
    const [selectedCartId, setSelectedCartId] = useState(null);
    const [imagem, setImagem] = useState(null);
    const [loading, setLoading] = useState(true);
    const [imageReady, setImageReady] = useState(false);
    const [downloadingCart, setDownloadingCart] = useState(false);
    const [downloadingValid, setDownloadingValid] = useState(false);
    const [scrolled, setScrolled] = useState(false);

    const carteirinhas = estudante?.carteirinhas || [];

    const userData = useMemo(() => {
        if (!estudante) return null;
        const cart = carteirinhas.find(c => String(c.id) === String(selectedCartId)) || carteirinhas[0] || {};
        return { ...estudante, ...cart, carteirinhaId: cart.id };
    }, [estudante, selectedCartId]);

    const walletStatus = useMemo(() => {
        if (['active', 'warning', 'expired'].includes(simular)) return simular; // override de teste
        return userData ? getWalletStatus(userData.validadeCarteirinha) : 'loading';
    }, [userData, simular]);
    const isExpired = walletStatus === 'expired';

    // Guard de sessão
    useEffect(() => {
        if (!Cookies.get("usuario") || !Cookies.get("cpf") || !Cookies.get("user_id")) {
            navigate('/login', { replace: true });
        }
    }, [navigate]);

    // Nav: sólida ao rolar
    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 24);
        window.addEventListener('scroll', onScroll, { passive: true });
        return () => window.removeEventListener('scroll', onScroll);
    }, []);

    useEffect(() => {
        async function loadData() {
            try {
                const res = await getInfoAlunoByCpf({ cpf });
                if (res?.data?.status !== false) {
                    setEstudante(res.data);
                    const carts = res.data.carteirinhas || [];
                    setSelectedCartId(carts[0]?.id ?? null);
                }
            } catch (err) {
                console.error(err);
            } finally {
                setLoading(false);
            }
        }
        loadData();
    }, [cpf]);

    useEffect(() => {
        if (!userData?.carteirinhaId) return;
        // Vencida: não baixa nem exibe a imagem (mostra o aviso no lugar).
        if (isExpired) { setImagem(null); setImageReady(false); return; }
        setImageReady(false);
        let url = null;
        getUserCarteirinhaByCpfPngApi({ cpf, carteirinhaId: userData.carteirinhaId })
            .then(imgRes => {
                url = URL.createObjectURL(imgRes.data);
                setImagem(url);
            })
            .catch(() => setImagem(null));
        return () => { if (url) URL.revokeObjectURL(url); };
    }, [selectedCartId, estudante, cpf, isExpired]);

    const firstName = userData?.nome ? userData.nome.split(' ')[0] : 'Estudante';

    const logout = () => {
        Cookies.remove("usuario");
        Cookies.remove("cpf");
        Cookies.remove("user_id");
        navigate('/', { replace: true });
    };

    const baixarCarteira = async () => {
        if (downloadingCart || !imageReady || isExpired) return;
        try {
            setDownloadingCart(true);
            await generatePdfCarteirinhaDigitalApi({ cpf: userData.cpf, carteirinhaId: userData.carteirinhaId });
        } catch (e) {
            console.error("Erro ao baixar PDF:", e);
        } finally {
            setTimeout(() => setDownloadingCart(false), 800);
        }
    };

    const baixarComprovante = async () => {
        if (downloadingValid || isExpired) return;
        try {
            setDownloadingValid(true);
            await generatePdfValidadorDigitalApi({ cpf: userData.cpf, carteirinhaId: userData.carteirinhaId });
        } catch (e) {
            console.error("Erro na validação:", e);
        } finally {
            setTimeout(() => setDownloadingValid(false), 800);
        }
    };

    const Nav = (
        <nav className={`vc-nav ${scrolled ? 'is-solid' : ''}`}>
            <div className="vc-wrap vc-nav__inner">
                <div className="vc-brand">
                    <img src={`${process.env.PUBLIC_URL}/nomeUnae.png`} alt="UNAE Estudantil" className="vc-brand__img" />
                </div>
                <div className="vc-nav__user">
                    <span className="vc-nav__name">{userData?.nome || 'Estudante'}</span>
                    <button className="vc-logout" onClick={logout}>
                        <LogOut size={15} /> Sair
                    </button>
                </div>
            </div>
        </nav>
    );

    if (loading) {
        return (
            <div className="vc">
                {Nav}
                <div className="vc-hero" style={{ flex: 1, display: 'grid', placeItems: 'center' }}>
                    <div className="vc-spin vc-spin--light vc-spin--lg" />
                </div>
            </div>
        );
    }

    return (
        <div className="vc">
            {Nav}

            {/* ===== HERO ===== */}
            <header className="vc-hero">
                <div className="vc-wrap">
                    <div className="vc-hero__main">
                        {/* coluna esquerda (desktop): título → ações */}
                        <div className="vc-hero__intro">
                            <div className={`vc-eyebrow ${isExpired ? 'vc-eyebrow--expired' : ''}`}><IdCard size={15} /> Carteira Estudantil Digital</div>
                            <h1 className="vc-hero__name">{firstName}</h1>
                            <div className="vc-hero__meta">
                                {(isExpired
                                    ? [userData?.ano]
                                    : [userData?.instituicao, userData?.ano]
                                ).filter(Boolean).join(' · ') || 'Identidade estudantil UNAE'}
                            </div>

                            <div>
                                <span className={`vc-pill vc-pill--${walletStatus}`}>
                                    <span className="vc-pill__dot" />
                                    {STATUS_TEXT[walletStatus] || STATUS_TEXT.loading}
                                </span>
                            </div>

                            {carteirinhas.length > 1 && (
                                <div className="vc-years">
                                    {carteirinhas.map(c => (
                                        <button
                                            key={c.id}
                                            type="button"
                                            className={`vc-year ${String(c.id) === String(selectedCartId) ? 'is-active' : ''}`}
                                            onClick={() => setSelectedCartId(c.id)}
                                        >
                                            {c.ano || '—'}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* palco do card (desktop: direita) */}
                        <div className="vc-stage">
                            <div className={`vc-cardframe ${isExpired ? 'is-expired' : ''}`}>
                                {isExpired ? (
                                    <div className="vc-expired">
                                        <div className="vc-expired__x"><XCircle size={48} /></div>
                                        <div className="vc-expired__title">Carteirinha vencida</div>
                                        <p className="vc-expired__text">
                                            Sua carteirinha está vencida, por isso não pode ser exibida nem baixada.
                                            Entre em contato para regularizar e renovar o seu documento.
                                        </p>
                                        <a className="vc-expired__btn" href={contatoUrl(userData?.cpf)} target="_blank" rel="noreferrer">
                                            <MessageCircle size={18} /> Entrar em contato
                                        </a>
                                    </div>
                                ) : (
                                    <>
                                        {imagem && (
                                            <img
                                                src={imagem}
                                                onLoad={() => setImageReady(true)}
                                                className={`vc-cardframe__img ${imageReady ? '' : 'is-loading'}`}
                                                alt="Carteira estudantil"
                                            />
                                        )}
                                        {imagem && imageReady && <div className="vc-cardframe__shine" />}
                                        {(!imagem || !imageReady) && (
                                            <div className="vc-skeleton">
                                                <div className="vc-spin vc-spin--light" />
                                                <span>Gerando carteirinha…</span>
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                        </div>

                        {/* ações */}
                        <div className="vc-actions">
                            <button
                                className={`vc-btn ${isExpired ? 'vc-btn--invalid' : 'vc-btn--gold'}`}
                                onClick={baixarCarteira}
                                disabled={!imageReady || isExpired || downloadingCart}
                            >
                                {downloadingCart ? <span className="vc-spin" /> : <Download size={18} />}
                                {downloadingCart ? 'Gerando…' : isExpired ? 'Indisponível' : 'Baixar carteira'}
                            </button>
                            {COMPROVANTE_VALIDACAO_ATIVO && (
                                <button
                                    className="vc-btn vc-btn--glass"
                                    onClick={baixarComprovante}
                                    disabled={isExpired || downloadingValid}
                                >
                                    {downloadingValid ? <span className="vc-spin vc-spin--light" /> : <ShieldCheck size={18} />}
                                    {downloadingValid ? 'Processando…' : 'Comprovante de validação'}
                                </button>
                            )}
                        </div>
                    </div>

                    {/* dados + renovação (largura cheia) */}
                    <CardInfoCarteira userData={userData} walletStatus={walletStatus} />
                </div>
            </header>

            {/* ===== 01 · BENEFÍCIOS ===== */}
            <section className="vc-band">
                <div className="vc-wrap">
                    <BeneficiosEAutenticacao />
                </div>
            </section>

            {/* ===== 02 · AUTENTICIDADE ===== */}
            <section className="vc-band vc-band--alt">
                <div className="vc-wrap">
                    <CardValidacaoDigital comprovanteAtivo={COMPROVANTE_VALIDACAO_ATIVO} />
                </div>
            </section>

            <Footer />
        </div>
    );
}
